import { useCallback, useMemo } from 'react';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';
import { useShallow } from 'zustand/react/shallow';
import { useOptionalAuth } from '@/shared/hooks/use-optional-auth';
import { isValidMessage } from '../lib/message-handlers';
import { streamChat } from '../api/stream-chat';
import { generateUUID } from '@/shared/utils/uuid';
import i18n from '@/shared/lib/i18n';
import type { UseTextChatReturn } from '../types/text-chat';

/**
 * 텍스트 채팅 훅 - HTTP Streaming 방식 (voice_server 우회)
 *
 * 텍스트 모드에서는 voice_server를 거치지 않고
 * 백엔드 /stream 엔드포인트를 직접 호출합니다.
 */
export const useTextChat = (): UseTextChatReturn => {
  const { messages, isLoading } = useRealtimeAgentStore(
    useShallow((state) => ({
      messages: state.messages,
      isLoading: state.isLoading,
    }))
  );

  const {
    addUserMessage,
    addStreamingAgentMessage,
    updateStreamingAgentMessage,
    finalizeStreamingMessage,
    setLoading,
  } = useRealtimeAgentStore(
    useShallow((state) => ({
      addUserMessage: state.addUserMessage,
      addStreamingAgentMessage: state.addStreamingAgentMessage,
      updateStreamingAgentMessage: state.updateStreamingAgentMessage,
      finalizeStreamingMessage: state.finalizeStreamingMessage,
      setLoading: state.setLoading,
    }))
  );

  const agentId = useRealtimeAgentStore(state => state.agentId);
  const wsSessionId = useRealtimeAgentStore(state => state.sessionId);
  const displayMode = useRealtimeAgentStore(state => state.displayMode);
  const { userId } = useOptionalAuth();

  // WebSocket sessionId가 없으면 안정적 fallback UUID 사용
  // → 매 요청 동일 session_id 보장 → Redis 키 일치 (인터넷 검색 폴백)
  const fallbackSessionId = useMemo(() => generateUUID(), []);
  const sessionId = wsSessionId || fallbackSessionId;

  /**
   * HTTP streaming 공통 로직
   */
  const callStream = useCallback((message: string, turnId: string) => {
    streamChat(
      {
        agentId: agentId || '',
        message,
        sessionId: sessionId || undefined,
        language: i18n.language || 'ko',
        displayMode: displayMode || 'web',
        userId: userId || 'public_expo_user',
      },
      {
        onChunk: (accumulatedText, isFirstChunk) => {
          if (isFirstChunk) {
            addStreamingAgentMessage(accumulatedText, turnId);
          } else {
            updateStreamingAgentMessage(accumulatedText, turnId);
          }
        },
        onComplete: (fullText) => {
          if (fullText) {
            finalizeStreamingMessage(fullText, turnId);
          }
          setLoading(false);
        },
        onError: (error) => {
          console.error('[TextChat] Streaming error:', error);
          finalizeStreamingMessage(
            '죄송합니다. 오류가 발생했습니다. 다시 시도해주세요.',
            turnId
          );
          setLoading(false);
        },
      }
    );
  }, [agentId, sessionId, displayMode, userId,
      addStreamingAgentMessage, updateStreamingAgentMessage, finalizeStreamingMessage, setLoading]);

  /**
   * 사용자 메시지 전송 (사용자 메시지 표시 + 로딩 + 스트리밍)
   */
  const sendMessage = useCallback((message: string) => {
    if (!message.trim() || isLoading) return;
    if (!isValidMessage(message)) return;

    const turnId = generateUUID();

    addUserMessage(message);
    setLoading(true);

    // "..." 로딩 인디케이터 렌더링 후 스트리밍 시작
    setTimeout(() => callStream(message, turnId), 500);
  }, [isLoading, addUserMessage, setLoading, callStream]);

  /**
   * 인사말 전송 (사용자 메시지 없이 에이전트 응답만 표시)
   */
  const sendGreeting = useCallback((message: string) => {
    if (isLoading) return;

    const turnId = generateUUID();
    setLoading(true);
    callStream(message, turnId);
  }, [isLoading, setLoading, callStream]);

  return {
    messages,
    isLoading,
    isConnected: true,
    sendMessage,
    sendGreeting,
  };
};
